import React, { useState } from "react";
import { useVehicle } from "../contexts/VehicleContext";
import { ConfirmationDialog } from "./confirmation-dialog";
import { PlusCircle, Trash, X } from "./icons";

const Dialog = ({ children, open, onOpenChange }: any) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-zinc-900 rounded-lg shadow-lg max-w-md w-full relative">
        {children}
        <div className="absolute top-0 right-0 p-4">
          <button onClick={() => onOpenChange(false)} className="text-zinc-400 hover:text-white focus:outline-none">
            <X size={24} />
          </button>
        </div>
      </div>
    </div>
  );
};

const DialogContent = ({ children, className }: any) => (
  <div className={`p-6 ${className}`}>{children}</div>
);

const DialogHeader = ({ children }: any) => <div className="mb-4">{children}</div>;
const DialogTitle = ({ children }: any) => <h2 className="text-xl font-bold">{children}</h2>;
const DialogDescription = ({ children }: any) => <p className="text-zinc-400 text-sm">{children}</p>;
const DialogFooter = ({ children }: any) => <div className="mt-6 flex justify-end gap-2">{children}</div>;

const Button = ({ children, className, onClick, type }: any) => (
  <button
    className={`px-4 py-2 rounded font-medium focus:outline-none ${className}`}
    onClick={onClick}
    type={type}
  >
    {children}
  </button>
);

interface ManageVehiclesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddVehicle?: () => void;
}

export function ManageVehiclesDialog({ open, onOpenChange, onAddVehicle }: ManageVehiclesDialogProps) {
  const { vehicles, removeVehicle } = useVehicle();
  const [vehicleToDelete, setVehicleToDelete] = useState<{ id: string; name: string } | null>(null);

  const handleDeleteVehicle = async () => {
    if (vehicleToDelete) {
      await removeVehicle(vehicleToDelete.id);
      setVehicleToDelete(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[425px] bg-zinc-900 text-white border-zinc-700">
          <DialogHeader>
            <DialogTitle>Manage Vehicles</DialogTitle>
            <DialogDescription></DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {vehicles.length === 0 && (
              <div className="text-zinc-400 text-sm">No vehicles saved.</div>
            )}
            {vehicles.map((vehicle) => (
              <div
                key={vehicle.id}
                className="flex items-center justify-between bg-zinc-800 rounded p-2"
              >
                <span className="truncate">{vehicle.name}</span>
                <Button
                  className="text-red-400 hover:text-red-500"
                  onClick={() => setVehicleToDelete({ id: vehicle.id, name: vehicle.name })}
                  type="button"
                >
                  <Trash />
                </Button>  
              </div>
            ))}
            {onAddVehicle && (
              <Button
                className="flex items-center text-emerald-500 hover:text-emerald-400 hover:bg-zinc-800 justify-start px-2 h-7 w-full bg-transparent"
                onClick={() => {
                  onAddVehicle();
                  onOpenChange(false);
                }}
                type="button"
              >
                <PlusCircle />
                Add Vehicle
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              className="bg-zinc-700 hover:bg-zinc-600 text-white"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={!!vehicleToDelete}
        onOpenChange={() => setVehicleToDelete(null)}
        title="Delete Vehicle"
        description={`Are you sure you want to delete "${vehicleToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteVehicle}
        destructive={true}
      />
    </>
  );
}